import { Directive, ElementRef, Input, Renderer2 } from '@angular/core';

/**
 * Makes the host element and its whole subtree unreachable while the bound
 * condition is true, by toggling the native `inert` attribute.
 *
 * Use it on the content a blocking overlay covers. A scrim only blocks the
 * mouse: without this, Tab still walks behind it and Enter can activate what it
 * lands on. `inert` is handled by the browser itself — it removes the subtree
 * from the tab order, swallows pointer events and hides it from assistive
 * technology — so there is no listener and nothing runs per keystroke.
 *
 * ```html
 * <div class="page-content" [appInertWhen]="isPageBlocked"> … </div>
 * ```
 *
 * Prefer this over binding `[attr.inert]` directly: `inert` is a boolean
 * attribute, so the natural-looking `[attr.inert]="blocked"` renders
 * `inert="false"` when blocked is false — which still marks the element inert.
 * The correct `blocked ? '' : null` form is easy to get wrong, and repeating it
 * across templates invites exactly that bug.
 */
@Directive({
  selector: '[appInertWhen]'
})
export class InertWhenDirective {

  @Input({ required: true })
  set appInertWhen(blocked: boolean) {
    const element = this.elementRef.nativeElement;
    if (blocked) {
      this.renderer.setAttribute(element, 'inert', '');
    } else {
      this.renderer.removeAttribute(element, 'inert');
    }
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) { }
}
